import type { ReactNode } from 'react'
import type { BlockerFunction } from 'react-router-dom'
import { StickySaveBar } from '@/components/ui/sticky-save-bar'

import { UnsavedChangesGuard } from './UnsavedChangesGuard'

interface UnsavedChangesBarProps {
  /** True while there are edits to save; shows the bar and arms the guard. */
  isDirty: boolean
  /**
   * Persist the edits. The guard waits for it before leaving the page; a
   * save that resolves `false` or throws keeps the user on the page.
   */
  onSave: () => unknown
  /** Drop the edits back to the saved values. */
  onDiscard?: () => void
  isSaving?: boolean
  /** False keeps Save disabled (e.g. a required field is empty). */
  canSave?: boolean
  /** Noun for the leave-page dialog, e.g. "account" → "unsaved account changes". */
  noun?: string
  message?: ReactNode
  saveLabel?: string
  error?: ReactNode
  errorDetail?: ReactNode
  /**
   * Set false when another guard already covers this page (react-router
   * supports one blocker at a time).
   */
  guard?: boolean
  /** Which in-app navigations the guard asks about; see UnsavedChangesGuard. */
  shouldBlock?: BlockerFunction
}

/**
 * The standard way a settings surface saves buffered edits: the floating save
 * bar while there is something to save, plus a guard that asks before leaving
 * the page (in-app navigation and closing the tab) with unsaved changes.
 */
export function UnsavedChangesBar({
  isDirty,
  onSave,
  onDiscard,
  isSaving = false,
  canSave = true,
  noun,
  message,
  saveLabel,
  error,
  errorDetail,
  guard = true,
  shouldBlock,
}: UnsavedChangesBarProps) {
  return (
    <>
      <StickySaveBar
        visible={isDirty || isSaving || Boolean(error)}
        onSave={() => void onSave()}
        onDiscard={onDiscard}
        isSaving={isSaving}
        canSave={canSave}
        message={message}
        saveLabel={saveLabel}
        error={error}
        errorDetail={errorDetail}
      />
      {guard && (
        <UnsavedChangesGuard
          when={isDirty}
          onSave={onSave}
          onDiscard={onDiscard}
          isSaving={isSaving}
          noun={noun}
          shouldBlock={shouldBlock}
        />
      )}
    </>
  )
}
